import { useNavigation } from '@/composables/navigation'
import { useUserManager } from '@/plugins/usermanager.plugin'
import { getAdminPortalUserProfile, loginAdminPortalUser } from '@/services/core.service'
import type { IAdminPortalUserProfile } from '@/types/auth'
import { AdminRole, AppManagerRoles, AppOperatorRoles, AppViewerRoles } from '@/types/core/Roles'
import * as Sentry from '@sentry/vue'
import { AxiosError, HttpStatusCode } from 'axios'
import type { User } from 'oidc-client-ts'
import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

const EOS_ROLES = [AppManagerRoles.EOS_MANAGER] as string[]
const RCAPP_ROLES = [AppManagerRoles.RCAPP_MANAGER, AppOperatorRoles.RCAPP_OPERATOR] as string[]
const ASICW_OPERATIONAL_ROLES = [AppManagerRoles.ASCIW_MANAGER, AppOperatorRoles.ASCIW_OPERATOR] as string[]
const ASICW_ROLES = [...ASICW_OPERATIONAL_ROLES, AppViewerRoles.ASICW_VIEWER]
const HVLAPP_ROLES = [AppManagerRoles.HVLAPP_MANAGER, AppOperatorRoles.HVLAPP_OPERATOR] as string[]
const MANAGER_ROLES = [
  AppManagerRoles.EOS_MANAGER,
  AppManagerRoles.RCAPP_MANAGER,
  AppManagerRoles.HVLAPP_MANAGER,
  AppManagerRoles.ASCIW_MANAGER,
] as string[]

/**
 * OIDC store for managing user authentication and profile data.
 * `user` is the OIDC (Zitadel) user object, and gets cached locally.
 * `profile` is the user's profile data from the Admin Portal API.
 */
export const useOIDCStore = defineStore('oidc', () => {
  const { logger } = Sentry

  const { goToLogin, goToHome } = useNavigation()
  const userManager = useUserManager()

  /** User (user OIDC info, from Zitadel). */
  const user = ref<User | null>(null)
  /** Profile (user application info, from Admin Portal back-end). */
  const profile = ref<IAdminPortalUserProfile | null>(null)

  /** Initialization state. */
  const isInitialized = ref(false)
  let initPromise: Promise<void> | null = null

  const isExpirationLoggingOut = ref(false)

  /**
   * Handle cross-tab logout synchronization.
   * Uses both storage events and BroadcastChannel for maximum compatibility.
   */
  let broadcastChannel: BroadcastChannel | null = null

  const handleLogoutEvent = async () => {
    // Another tab has logged out, clear local state and redirect
    user.value = null
    profile.value = null
    await userManager.removeUser()
    goToLogin()
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === 'oidc-logout-event' && event.newValue) {
      handleLogoutEvent()
    }
  }

  const handleBroadcastMessage = (event: MessageEvent) => {
    if (event.data === 'oidc-logout') {
      handleLogoutEvent()
    }
  }

  // Add cross-tab synchronization listeners
  if (typeof window !== 'undefined') {
    // Use storage events (works across all browsers)
    window.addEventListener('storage', handleStorageChange)

    // Use BroadcastChannel for better performance (modern browsers)
    if ('BroadcastChannel' in window) {
      broadcastChannel = new BroadcastChannel('oidc-logout')
      broadcastChannel.addEventListener('message', handleBroadcastMessage)
    }
  }

  /**
   * UserManager `userLoaded` event callback:
   * - Loads user from `localStorage` into store.
   */
  userManager.events.addUserLoaded(async (loadedUser) => {
    user.value = loadedUser
    // `addUserLoaded` is intended to handle updates (like silent token renewals).
    // It should not handle navigation. If a user is filling out a form and their token silently refreshes,
    // this event fires and currently kicks them back to the dashboard, losing their work.
  })

  /**
   * UserManager `userUnloaded` event callback:
   * - Clears store user and profile.
   * - Redirects to login.
   */
  userManager.events.addUserUnloaded(() => {
    user.value = null
    profile.value = null
    goToLogin()
  })

  const hasAcceptedLegalDocuments = computed(
    () => profile.value?.eula_accepted && profile.value?.tos_accepted && profile.value?.privacy_policy_accepted
  )

  const isAdminUser = computed(() => {
    return profile.value?.roles.includes(AdminRole)
  })

  const isEOSUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => EOS_ROLES.includes(roles))
  })

  const isRCAppUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => RCAPP_ROLES.includes(roles))
  })

  const isRcAppManagerUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.includes(AppManagerRoles.RCAPP_MANAGER)
  })

  /**
   * True when it has the operator role **only**
   */
  const isRcAppOperator = computed(() => {
    return (
      profile.value?.roles.includes(AppOperatorRoles.RCAPP_OPERATOR) &&
      !profile.value?.roles.includes(AppManagerRoles.RCAPP_MANAGER)
    )
  })

  const isASICWAppUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => ASICW_ROLES.includes(roles))
  })

  const isASICWViewerUser = computed(() => {
    return profile.value?.roles.includes(AppViewerRoles.ASICW_VIEWER)
  })

  const isASICWOperationalUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => ASICW_OPERATIONAL_ROLES.includes(roles))
  })

  const isASICWManagerUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.includes(AppManagerRoles.ASCIW_MANAGER)
  })

  const isHVLAppUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => HVLAPP_ROLES.includes(roles))
  })

  const isHVLManagerUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.includes(AppManagerRoles.HVLAPP_MANAGER)
  })

  const isManagerUser = computed(() => {
    return isAdminUser.value || profile.value?.roles.some((roles) => MANAGER_ROLES.includes(roles))
  })

  const isCustomer = computed(() => {
    return profile.value?.tenant_id === import.meta.env.VITE_APP_ZITADEL_SUPER_REVOLVE_LABS_ORGANIZATION_ID
  })

  /**
   * Store initialization:
   * 1. Clears stale login attempts and loads user from local storage.
   * 2. If user is present, loads user profile from back-end.
   * 3. When finished, sets flag to end global loading state.
   * @returns {Promise<void>} - a `Promise` to track loading state.
   */
  const init = async (): Promise<void> => {
    // Return existing promise if already initializing
    if (initPromise) return initPromise

    initPromise = (async () => {
      try {
        // Check if there's a recent logout event from another tab
        const logoutEvent = localStorage.getItem('oidc-logout-event')
        if (logoutEvent) {
          // Clear the logout event and ensure user is logged out
          localStorage.removeItem('oidc-logout-event')
          await handleLogoutEvent()
          return
        }

        await userManager.clearStaleState()

        user.value = await userManager.getUser()

        if (user.value) {
          // Handle expiry
          if (user.value.expired) {
            isExpirationLoggingOut.value = true
            // User token is expired, log them out without storing another logout event
            // (otherwise we'd log out the user when sent back to /callback from Zitadel)
            await logout(false)
            await handleLogoutEvent()
            return
          } else {
            await loadUserProfile()
          }
        }
      } finally {
        isInitialized.value = true
      }
    })()

    return initPromise
  }

  /**
   * Cleanup function to remove event listeners.
   */
  const cleanup = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorageChange)
    }
    if (broadcastChannel) {
      broadcastChannel.removeEventListener('message', handleBroadcastMessage)
      broadcastChannel.close()
      broadcastChannel = null
    }
  }

  /**
   * Handles initial log-in event.
   * 1. Creates an OIDC sign-in request in local storage with `UserManager`.
   * 2. Redirects user to Zitadel to log in using their credentials.
   */
  const signIn = async () => {
    try {
      await userManager.signinRedirect()
    } catch (error) {
      logger.error('oidc-client-ts failed to authorize.', { error, log_source: 'oidc.store.ts' })
    }
  }

  /**
   * First authenticates with Zitadel using the `UserManager`,
   * then authenticates with Admin Portal, logging the user out of Zitadel on failure.
   *
   * If the Zitadel request fails for any reason (including missing local state),
   * the user is logged out of Zitadel and shown the login page.
   */
  const signInCallback = async () => {
    try {
      // Process callback
      const cbUser = await userManager.signinCallback()
      if (!cbUser) throw new Error('User from signinCallback was undefined.')

      await loginAdminPortalUser()
      await loadUserProfile()
      await goToHome()
    } catch (error) {
      logger.error('oidc-client-ts signinCallback failed.', { error, log_source: 'oidc.store.ts' })
      await logout()
    }
  }

  /**
   * Sign out of Zitadel silently in an IFrame (log to Sentry on error).
   */
  const logout = async (storeTrail = true) => {
    try {
      await userManager.signoutSilent()
    } catch (error) {
      logger.error('UserManager silent signout errored.', { error, log_source: 'oidc.store.ts' })
    } finally {
      isExpirationLoggingOut.value = false
      // Notify other tabs about logout using multiple methods
      if (storeTrail) localStorage.setItem('oidc-logout-event', Date.now().toString())

      // Also use BroadcastChannel if available
      if (broadcastChannel) {
        broadcastChannel.postMessage('oidc-logout')
      }

      await handleLogoutEvent()
    }
  }

  /**
   * Loads user profile data into store state from Admin Portal API.
   */
  const loadUserProfile = async () => {
    try {
      profile.value = await getAdminPortalUserProfile()
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.Unauthorized) {
        // Clear session and redirect to login
        await handleLogoutEvent()
      }
    }
  }

  /**
   * Returns a user's full, first, or last name depending on field population.
   * @returns {string} the full name of the user.
   */
  const getFullName = (): string => {
    let fullName = ''
    if (profile.value) {
      if (profile.value.first_name) {
        fullName += profile.value.first_name
      }
      if (profile.value.last_name) {
        if (fullName.length > 0) {
          fullName += ' '
        }
        fullName += profile.value.last_name
      }
    }

    return fullName
  }

  return {
    isInitialized: readonly(isInitialized),
    init,
    signIn,
    signInCallback,
    logout,
    handleLogoutEvent,
    cleanup,
    user: readonly(user),
    profile: readonly(profile),
    loadUserProfile,
    getFullName,

    // Authorization - will be refactored to use CASL
    // https://casl.js.org/v6/en/
    hasAcceptedLegalDocuments,
    isAdminUser,
    isEOSUser,
    isRCAppUser,
    isRcAppOperator,
    isRcAppManagerUser,
    isHVLAppUser,
    isHVLManagerUser,
    isManagerUser,
    isASICWAppUser,
    isASICWOperationalUser,
    isASICWViewerUser,
    isASICWManagerUser,
    isCustomer,
    isExpirationLoggingOut,
  }
})
